import { Button, Card, CardBody, CardHeader, Input } from '@heroui/react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export interface CheckoutAuthChoiceProps {
  onContinueToAddress: (guestEmail?: string, guestPhone?: string) => void;
}

const CheckoutAuthChoice: React.FC<CheckoutAuthChoiceProps> = ({
  onContinueToAddress,
}) => {
  const { isAuthenticated, user } = useAuth();
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [errors, setErrors] = useState<{
    guestEmail?: string;
    guestPhone?: string;
  }>({});

  const handleGuestContinue = () => {
    const newErrors: { guestEmail?: string; guestPhone?: string } = {};
    if (!guestEmail.trim()) {
      newErrors.guestEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(guestEmail)) {
      newErrors.guestEmail = 'Please enter a valid email address';
    }
    if (!guestPhone.trim()) {
      newErrors.guestPhone = 'Phone number is required for Royal Mail tracking';
    } else if (guestPhone.length > 20) {
      newErrors.guestPhone = 'Phone number is too long';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      onContinueToAddress(guestEmail.trim(), guestPhone.trim());
    }
  };

  // Logged-in user: show email and continue button
  if (isAuthenticated && user) {
    return (
      <Card>
        <CardHeader>
          <h2 className='text-xl font-semibold'>Account</h2>
        </CardHeader>
        <CardBody className='space-y-4'>
          <div className='flex items-center space-x-3'>
            <div className='w-2 h-2 bg-green-500 rounded-full' />
            <div>
              <p className='font-medium text-gray-900'>
                Signed in as {user.email}
              </p>
              <p className='text-sm text-gray-500'>
                Order confirmations will be sent to this email
              </p>
            </div>
          </div>
          <Button
            color='primary'
            size='lg'
            className='w-full'
            onClick={() => onContinueToAddress()}
          >
            Continue to checkout
          </Button>
        </CardBody>
      </Card>
    );
  }

  // Guest: collect email and phone
  return (
    <Card>
      <CardHeader>
        <h2 className='text-xl font-semibold'>
          How would you like to checkout?
        </h2>
      </CardHeader>
      <CardBody className='space-y-6'>
        <div className='space-y-4'>
          <Input
            type='email'
            label='Email'
            placeholder='Enter your email for order confirmation'
            value={guestEmail}
            onValueChange={setGuestEmail}
            isInvalid={!!errors.guestEmail}
            errorMessage={errors.guestEmail}
            isRequired
            variant='bordered'
          />
          <Input
            type='tel'
            label='Phone number'
            placeholder='Required for Royal Mail delivery tracking'
            value={guestPhone}
            onValueChange={setGuestPhone}
            isInvalid={!!errors.guestPhone}
            errorMessage={errors.guestPhone}
            isRequired
            variant='bordered'
          />
          <Button
            color='primary'
            size='lg'
            className='w-full'
            onClick={handleGuestContinue}
          >
            Continue as guest
          </Button>
        </div>

        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-200' />
          </div>
          <div className='relative flex justify-center text-sm'>
            <span className='px-2 bg-white text-gray-500'>
              Or create an account for faster checkout
            </span>
          </div>
        </div>

        <div className='flex flex-col sm:flex-row gap-3 justify-center'>
          <Button
            as={Link}
            to='/login?redirect=/checkout'
            state={{ from: { pathname: '/checkout' } }}
            variant='bordered'
            className='w-full sm:w-auto'
          >
            Sign in
          </Button>
          <Button
            as={Link}
            to='/register?redirect=/checkout'
            state={{ from: { pathname: '/checkout' } }}
            variant='bordered'
            className='w-full sm:w-auto'
          >
            Create account
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default CheckoutAuthChoice;
